import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import {
  ApprovalNumberResponse,
  BreedResponse,
  CellLineResponse,
  IncubatorResponse,
  SpeciesResponse,
  StringMapItemResponse,
  TechnicianItem,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchStringMap({
  objectTypeCode,
  name,
}: {
  objectTypeCode: number;
  name: string;
}): Promise<StringMapItemResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('objectTypeCode', String(objectTypeCode));
  searchParams.set('name', name);

  const url = `${getBaseUrl()}/api/v1/lookups/string-map?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as StringMapItemResponse[];
}

async function fetchIncubators({
  siteId,
}: {
  siteId: string;
}): Promise<IncubatorResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('siteId', siteId);

  const url = `${getBaseUrl()}/api/v1/lookups/incubators?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as IncubatorResponse[];
}

export async function fetchBreeds({
  search,
  subFamilies,
}: { search?: string; subFamilies?: number[] } = {}): Promise<BreedResponse[]> {
  const searchParams = new URLSearchParams();
  if (search) {
    searchParams.set('search', search);
  }
  if (subFamilies && subFamilies.length > 0) {
    subFamilies.forEach((sf) => searchParams.append('subFamilies', String(sf)));
  }
  const query = searchParams.toString();
  const url = `${getBaseUrl()}/api/v1/lookups/breeds${query ? `?${query}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as BreedResponse[];
}
export const breedsOptions = ({
  search,
  subFamilies,
}: { search?: string; subFamilies?: number[] } = {}) =>
  queryOptions({
    queryKey: ['lookups', 'breeds', 'list', { search, subFamilies }],
    async queryFn() {
      return fetchBreeds({ search, subFamilies });
    },
  });

export async function fetchSpecies(): Promise<SpeciesResponse[]> {
  const url = `${getBaseUrl()}/api/v1/lookups/species`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as SpeciesResponse[];
}

export async function fetchBreedById(id: string): Promise<BreedResponse> {
  const url = `${getBaseUrl()}/api/v1/lookups/breeds/${id}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as BreedResponse;
}

export const speciesOptions = () =>
  queryOptions({
    queryKey: ['lookups', 'species', 'list'],
    async queryFn() {
      return fetchSpecies();
    },
  });

export const stringMapOptions = ({
  objectTypeCode,
  name,
}: {
  objectTypeCode: number;
  name: string;
}) =>
  queryOptions({
    queryKey: ['lookups', 'string-map', { objectTypeCode, name }],
    async queryFn() {
      return fetchStringMap({ objectTypeCode, name });
    },
  });

export const incubatorsOptions = ({ siteId }: { siteId: string }) =>
  queryOptions({
    queryKey: ['lookups', 'incubators', 'list', { siteId }],
    async queryFn() {
      return fetchIncubators({ siteId });
    },
  });

async function fetchVeterinarians(): Promise<TechnicianItem[]> {
  const url = `${getBaseUrl()}/api/v1/lookups/technicians`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as TechnicianItem[];
}

export const veterinariansOptions = () =>
  queryOptions({
    queryKey: ['lookups', 'technicians', 'list'],
    async queryFn() {
      return fetchVeterinarians();
    },
  });

async function fetchEquipmentById(id: string): Promise<TechnicianItem | null> {
  const url = `${getBaseUrl()}/api/v1/lookups/equipment/${encodeURIComponent(id)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as TechnicianItem;
}

export const equipmentByIdOptions = (id: string) =>
  queryOptions({
    queryKey: ['lookups', 'equipment', id],
    async queryFn() {
      return fetchEquipmentById(id);
    },
  });

export const cellLinesOptions = ({ search }: { search?: string } = {}) =>
  queryOptions({
    queryKey: ['lookups', 'cell-lines', 'list', { search }],
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (search) {
        searchParams.set('search', search);
      }
      const query = searchParams.toString();
      const url = `${getBaseUrl()}/api/v1/lookups/cell-lines${query ? `?${query}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: getHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as CellLineResponse[];
    },
  });

const STORAGE_LOCATIONS_PAGE_SIZE = 50;

async function fetchStorageLocations({
  search,
  afterName,
  afterId,
  siteIds,
  take = STORAGE_LOCATIONS_PAGE_SIZE,
}: {
  search?: string;
  afterName?: string;
  afterId?: string;
  siteIds?: string[];
  take?: number;
}): Promise<{ id: string; name: string | null; siteId: string | null }[]> {
  const searchParams = new URLSearchParams();
  if (search) {
    searchParams.set('search', search);
  }
  if (afterName && afterId) {
    searchParams.set('afterName', afterName);
    searchParams.set('afterId', afterId);
  }
  siteIds?.forEach((siteId) => searchParams.append('siteIds', siteId));
  searchParams.set('take', String(take));

  const url = `${getBaseUrl()}/api/v1/lookups/storage-locations?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as {
    id: string;
    name: string | null;
    siteId: string | null;
  }[];
}

type StorageLocationsPageParam = { afterName: string; afterId: string } | null;

export const storageLocationsOptions = ({
  search,
  siteIds,
}: { search?: string; siteIds?: string[] } = {}) =>
  infiniteQueryOptions({
    queryKey: ['lookups', 'storage-locations', 'list', { search, siteIds }],
    initialPageParam: null as StorageLocationsPageParam,
    queryFn: ({ pageParam }) =>
      fetchStorageLocations({
        search,
        siteIds,
        afterName: pageParam?.afterName,
        afterId: pageParam?.afterId,
      }),
    getNextPageParam: (lastPage): StorageLocationsPageParam | undefined => {
      if (lastPage.length < STORAGE_LOCATIONS_PAGE_SIZE) {
        return undefined;
      }
      const lastRow = lastPage[lastPage.length - 1];
      return lastRow.name
        ? { afterName: lastRow.name, afterId: lastRow.id }
        : undefined;
    },
    staleTime: 5 * 60 * 1000,
  });

export const approvalNumbersOptions = ({ search }: { search?: string } = {}) =>
  queryOptions({
    queryKey: ['lookups', 'approval-numbers', 'list', { search }],
    async queryFn() {
      const searchParams = new URLSearchParams();
      if (search) {
        searchParams.set('search', search);
      }
      const query = searchParams.toString();
      const url = `${getBaseUrl()}/api/v1/lookups/approval-numbers${query ? `?${query}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: getHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as ApprovalNumberResponse[];
    },
  });
